import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, FileText, ChevronRight, RefreshCw, CheckCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoiceList = () => {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/invoice`, { withCredentials: true });
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F5F7]" data-testid="invoice-list-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#0A0A0A] transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('common.back')}
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchInvoices}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">{t('dashboard.invoice.check')}</h1>
          <Link to="/invoice/new">
            <Button className="bg-[#0047FF] hover:bg-[#003BCC]" data-testid="new-invoice-btn">
              {t('dashboard.invoice.new')}
            </Button>
          </Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#0047FF]"></div>
          </div>
        ) : invoices.length === 0 ? (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-12 text-center">
            <FileText className="w-12 h-12 text-[#A1A1AA] mx-auto mb-4" />
            <p className="text-[#52525B]">No tienes solicitudes de factura</p>
            <Link to="/invoice/new">
              <Button className="mt-4 bg-[#0047FF] hover:bg-[#003BCC]">
                {t('dashboard.invoice.new')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {invoices.map((invoice, index) => (
              <motion.div
                key={invoice.invoice_request_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div 
                  className="bg-white rounded-xl border border-[#E5E7EB] p-5"
                  data-testid={`invoice-item-${index}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-mono text-sm text-[#52525B]">
                          #{invoice.invoice_request_id?.slice(0, 12).toUpperCase()}
                        </span>
                        {invoice.status === 'Completada' ? (
                          <span className="status-badge status-completed flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            {invoice.invoice_number || 'Completada'}
                          </span>
                        ) : (
                          <span className="status-badge status-pending flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {t('invoice.pending')}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-lg mb-1">{invoice.product_name}</h3>
                      <p className="text-sm text-[#52525B]">
                        {invoice.purchase_source} • Pedido: {invoice.order_id}
                      </p>
                      <p className="text-sm text-[#52525B]">
                        {invoice.full_name} • NIF: {invoice.nif_cif_dni}
                      </p>
                      <p className="text-xs text-[#A1A1AA] mt-2">
                        {new Date(invoice.created_at).toLocaleDateString('es-ES', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                      {invoice.price && (
                        <p className="text-sm font-semibold text-[#0047FF] mt-2">
                          Total: {(invoice.price * (invoice.quantity || 1) * 1.21).toFixed(2)} € (IVA incl.)
                        </p>
                      )}
                    </div>
                    {invoice.status === 'Completada' && (
                      <div className="w-10 h-10 bg-[#10B981]/10 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-[#10B981]" />
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default InvoiceList;
